import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Send, Plus, Clock, CheckCircle2, XCircle, AlertCircle, Trash2 } from "lucide-react";
import { listCampaigns, deleteCampaign } from "@/lib/admin-api";
import { toast } from "sonner";
import { formatMtyDateTimeShort as fmtMTY } from "@/lib/mty-date";

const STATUS_LABELS = {
  draft: { label: "Borrador", color: "bg-neutral-100 text-neutral-700", icon: AlertCircle },
  scheduled: { label: "Programada", color: "bg-blue-100 text-blue-800", icon: Clock },
  sending: { label: "Enviando...", color: "bg-orange-100 text-orange-800", icon: Send },
  sent: { label: "Enviada", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  canceled: { label: "Cancelada", color: "bg-neutral-200 text-neutral-600", icon: XCircle },
};

export default function AdminCampaigns() {
  const [list, setList] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const data = await listCampaigns({ limit: 100 });
      setList(data);
    } catch (e) {
      toast.error("Error cargando campañas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const doDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta campaña? Si ya se envió, se borrará su histórico.")) return;
    try {
      await deleteCampaign(id);
      toast.success("Eliminada");
      load();
    } catch {
      toast.error("Error eliminando");
    }
  };

  return (
    <div className="p-5 md:p-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="section-title text-3xl md:text-4xl font-black flex items-center gap-3">
            <Send className="w-8 h-8 text-red-600" /> Campañas
          </h1>
          <p className="text-neutral-500 text-sm mt-1">Envíos masivos, A/B testing y programación</p>
        </div>
        <button onClick={() => nav("/admin/campaigns/new")} className="btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm" data-testid="new-campaign-btn">
          <Plus className="w-4 h-4" /> Nueva campaña
        </button>
      </div>

      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-neutral-500">Cargando...</div>
        ) : list.items.length === 0 ? (
          <div className="p-16 text-center text-neutral-500">
            <Send className="w-10 h-10 mx-auto text-neutral-300 mb-3" />
            <p className="font-semibold text-neutral-700">Aún no tienes campañas</p>
            <p className="text-sm mt-1">Crea tu primera campaña para empezar a enviar novedades.</p>
            <button onClick={() => nav("/admin/campaigns/new")} className="mt-4 btn-primary px-5 py-2.5 rounded-lg text-sm inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Crear campaña
            </button>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="campaigns-table">
            <thead className="bg-neutral-50 text-neutral-600 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3">Nombre</th>
                <th className="text-left px-4 py-3">Asunto</th>
                <th className="text-left px-4 py-3">A/B</th>
                <th className="text-left px-4 py-3">Estado</th>
                <th className="text-left px-4 py-3">Audiencia</th>
                <th className="text-left px-4 py-3">Entregados</th>
                <th className="text-left px-4 py-3">Fallidos</th>
                <th className="text-left px-4 py-3">Creada</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {list.items.map((c) => {
                const S = STATUS_LABELS[c.status] || STATUS_LABELS.draft;
                return (
                  <tr key={c.id} className="border-t border-neutral-100 hover:bg-neutral-50 cursor-pointer" onClick={() => nav(`/admin/campaigns/${c.id}`)}>
                    <td className="px-4 py-3 font-semibold">{c.name}</td>
                    <td className="px-4 py-3 text-neutral-600 max-w-[280px] truncate">{c.subject_a}</td>
                    <td className="px-4 py-3">{c.ab_enabled ? <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-800 rounded-full font-semibold">A/B</span> : "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full font-semibold inline-flex items-center gap-1 ${S.color}`}>
                        <S.icon className="w-3 h-3" /> {S.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono">{c.stats?.recipients ?? 0}</td>
                    <td className="px-4 py-3 font-mono text-green-700">{c.stats?.sent ?? 0}</td>
                    <td className="px-4 py-3 font-mono text-red-700">{c.stats?.failed ?? 0}</td>
                    <td className="px-4 py-3 text-xs text-neutral-500">{fmtMTY(c.created_at)}</td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => doDelete(c.id)} className="text-red-600 hover:bg-red-50 p-1.5 rounded" aria-label="Eliminar" data-testid={`del-camp-${c.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
